import React, { type ReactElement } from "react";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { GenericTooltip } from "@components/tooltip-container";
import { type MetricFilters } from "@features/sales-v3/hooks/use-chart-state";

interface FiltersTooltipProps {
  metricFilters: MetricFilters[];
  ignoreTableFilters: boolean;
  children: ReactElement;
  canShowTooltip: boolean;
}

export const FiltersTooltip = ({
  metricFilters,
  ignoreTableFilters,
  children,
  canShowTooltip,
}: FiltersTooltipProps) => {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const hasSelectedFilters = metricFilters.length > 0;

  if (!hasSelectedFilters || !canShowTooltip) {
    return <>{children}</>;
  }

  return (
    <GenericTooltip
      placement="bottom"
      content={metricFilters.map(({ breakdown, values }) => {
        const breakdownConfig = breakdownConfigs.find(
          ({ key }) => key === breakdown.key,
        );
        const choicesWithoutNotSet = breakdownConfig?.choices.filter(
          (choice) => choice.value,
        );

        const countText =
          breakdownConfig && choicesWithoutNotSet
            ? ` (${values.length} of ${choicesWithoutNotSet.length})`
            : "";

        return (
          <div key={breakdown.key} className="mb-1.5 last:mb-0">
            {ignoreTableFilters && (
              <div className="text-xxs text-gray+2 mb-4">
                Ignore table filters
              </div>
            )}
            <div className="text-xxs text-gray+2 mb-1">
              {breakdown.name} {countText}
            </div>
            <div className="text-sm grid grid-cols-2 gap-x-2 gap-y-0.5">
              {values.map((value) => (
                <div key={value.value} className="mb-0.5 last:mb-0">
                  {value.label}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      noInfoCircle
      mouseEnterDelay={0.3}
    >
      {children}
    </GenericTooltip>
  );
};
